import { Link } from 'react-router-dom';
import { formatMoney } from '@teentack/shared';
import { useAuth } from '@/auth/use-auth';
import { useMyEarnings, useTeenOverview } from '@/features/teen/use-teen-overview';
import { TeenShell } from '@/features/teen/TeenShell';
import { EmptyState, StatTile } from '@/components/ui/Feedback';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  ArrowRightIcon,
  BadgeCheckIcon,
  BriefcaseIcon,
  ShieldCheckIcon,
  SparkleIcon,
  WalletIcon,
} from '@/components/ui/Icons';
import { presentApiError } from '@/lib/form-errors';
import { ReviewPrompt } from '@/features/reviews/ReviewPrompt';
import { GoalsPanel } from '@/features/goals/GoalsPanel';
import { TeenActivityPanel } from '@/features/teen/TeenActivityPanel';
import { TeenStatusBoard } from '@/features/teen/TeenStatusBoard';

/**
 * The teen's home. [Teen step 20]
 *
 * Ordered by who is blocked on whom: a job offer waiting on *you* comes first,
 * then permission you can act on, then what you are waiting on others for.
 */
export function TeenDashboardPage() {
  const { user } = useAuth();
  const overview = useTeenOverview();
  const earnings = useMyEarnings();
  const data = overview.data;

  const firstName = user?.displayName.split(' ')[0] ?? 'there';
  const needsYou = (data?.jobOffers ?? 0) + (data?.readyToApply ?? 0);

  /** Small, honest nudges — only shown when the thing is actually missing. */
  const profileGaps = data
    ? [
        { done: data.skillCount > 0, label: 'Add your skills' },
        { done: data.hasBio, label: 'Write a short bio' },
        { done: data.hasAvailability, label: 'Set your availability' },
      ].filter((gap) => !gap.done)
    : [];

  return (
    <TeenShell
      title={`Hey ${firstName}`}
      description={
        needsYou > 0
          ? `${needsYou} ${needsYou === 1 ? 'thing needs' : 'things need'} you right now.`
          : 'Nothing needs you right now — go find something good.'
      }
    >
      {overview.isPending && (
        <div className="space-y-6" aria-busy="true">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((n) => (
              <Skeleton key={n} className="h-28" />
            ))}
          </div>
          <Skeleton className="h-40" />
        </div>
      )}

      {overview.isError && (
        <div className="rounded-2xl border border-line bg-white p-8 text-center shadow-soft">
          <p className="text-sm text-slate-500">
            {presentApiError(overview.error, 'Could not load your dashboard').message}
          </p>
          <Button className="mt-5" onClick={() => void overview.refetch()}>
            Try again
          </Button>
        </div>
      )}

      {data && (
        <div className="space-y-8">
          {/* Money first — it is why anyone is here. */}
          <section className="overflow-hidden rounded-3xl border border-line bg-white shadow-lift">
            <div className="bg-brand-gradient px-6 py-5 sm:px-8">
              <p className="text-xs font-bold tracking-wide text-white/80 uppercase">
                Earned so far
              </p>
              <p className="mt-1 text-3xl font-extrabold text-white sm:text-4xl">
                {earnings.data
                  ? formatMoney(
                      earnings.data.totalEarned.amountMinor,
                      earnings.data.totalEarned.currency,
                    )
                  : '—'}
              </p>
            </div>
            <dl className="grid grid-cols-2 divide-x divide-line sm:grid-cols-3">
              <div className="px-6 py-4">
                <dt className="text-xs font-semibold text-slate-500">Awaiting payout</dt>
                <dd className="mt-1 text-lg font-extrabold text-ink">
                  {earnings.data
                    ? formatMoney(
                        earnings.data.pendingPayout.amountMinor,
                        earnings.data.pendingPayout.currency,
                      )
                    : '—'}
                </dd>
              </div>
              <div className="px-6 py-4">
                <dt className="text-xs font-semibold text-slate-500">Jobs finished</dt>
                <dd className="mt-1 text-lg font-extrabold text-ink">{data.completedJobs}</dd>
              </div>
              <div className="col-span-2 border-t border-line px-6 py-4 sm:col-span-1 sm:border-t-0">
                <dt className="text-xs font-semibold text-slate-500">Active right now</dt>
                <dd className="mt-1 text-lg font-extrabold text-ink">{data.activeJobs}</dd>
              </div>
            </dl>
          </section>

          {/* Every stage of the journey on one board. [Teen Phase 6] */}
          <TeenStatusBoard overview={data} />

          {/* Rating, recent activity, and the state of any job under way. */}
          <TeenActivityPanel />

          {/* Renders nothing when there is no finished work to rate. */}
          <ReviewPrompt />

          {/* Read-only here — a goal is the guardian's to set. */}
          <GoalsPanel canEdit={false} />

          {/* Needs you */}
          {needsYou > 0 && (
            <section className="rounded-2xl border border-sun-200 bg-sun-50 p-5 sm:p-6">
              <h2 className="text-base font-bold text-sun-800">Needs you</h2>
              <ul className="mt-4 space-y-2">
                {data.jobOffers > 0 && (
                  <li>
                    <Link
                      to="/my-work"
                      className="flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 text-sm font-semibold text-ink transition hover:bg-white/70"
                    >
                      <span>
                        {data.jobOffers} job {data.jobOffers === 1 ? 'offer' : 'offers'} to accept
                        or decline
                      </span>
                      <ArrowRightIcon className="size-4 text-brand-600" />
                    </Link>
                  </li>
                )}
                {data.readyToApply > 0 && (
                  <li>
                    <Link
                      to="/jobs"
                      className="flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 text-sm font-semibold text-ink transition hover:bg-white/70"
                    >
                      <span>
                        {data.readyToApply} job{data.readyToApply === 1 ? '' : 's'} your guardian
                        approved — you can apply now
                      </span>
                      <ArrowRightIcon className="size-4 text-brand-600" />
                    </Link>
                  </li>
                )}
              </ul>
            </section>
          )}

          {/* Where everything stands */}
          <section>
            <h2 className="mb-4 text-base font-bold text-ink">Where things stand</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                label="Job offers"
                value={data.jobOffers}
                tone={data.jobOffers > 0 ? 'attention' : 'neutral'}
                hint={data.jobOffers > 0 ? 'Waiting on your answer' : 'None right now'}
                icon={<BriefcaseIcon className="size-4 text-slate-400" />}
              />
              <StatTile
                label="Applications out"
                value={data.openApplications + data.shortlistedApplications}
                hint={
                  data.shortlistedApplications > 0
                    ? `${data.shortlistedApplications} shortlisted`
                    : 'Waiting on employers'
                }
                tone={data.shortlistedApplications > 0 ? 'good' : 'neutral'}
                icon={<BadgeCheckIcon className="size-4 text-slate-400" />}
              />
              <StatTile
                label="Waiting on guardian"
                value={data.awaitingGuardian}
                hint={
                  data.guardianName ? `${data.guardianName} has to approve` : 'Permission requests'
                }
                icon={<ShieldCheckIcon className="size-4 text-slate-400" />}
              />
              <StatTile
                label="Saved jobs"
                value={data.savedJobs}
                hint="Shortlisted for later"
                icon={<SparkleIcon className="size-4 text-slate-400" />}
              />
            </div>
          </section>

          {/* Profile nudges — only what is genuinely missing */}
          {profileGaps.length > 0 && (
            <section className="rounded-2xl border border-line bg-white p-5 shadow-soft sm:p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="text-base font-bold text-ink">Get picked more often</h2>
                  <p className="mt-1 max-w-lg text-sm leading-relaxed text-slate-500">
                    Employers search on skills and read your bio before they shortlist. These take a
                    minute each.
                  </p>
                </div>
                <Link
                  to="/profile"
                  className="inline-flex items-center gap-1.5 rounded-xl border border-line bg-white px-4 py-2.5 text-sm font-semibold text-ink transition hover:border-brand-300 hover:bg-brand-50"
                >
                  Edit profile
                  <ArrowRightIcon className="size-4" />
                </Link>
              </div>
              <ul className="mt-4 flex flex-wrap gap-2">
                {profileGaps.map((gap) => (
                  <li key={gap.label}>
                    <Badge tone="sun">{gap.label}</Badge>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* First-run state */}
          {data.jobOffers === 0 &&
            data.openApplications === 0 &&
            data.shortlistedApplications === 0 &&
            data.activeJobs === 0 && (
              <EmptyState
                icon={<WalletIcon className="size-6" />}
                title="Nothing on the go yet"
                action={
                  <Link
                    to="/jobs"
                    className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-700"
                  >
                    Browse opportunities
                    <ArrowRightIcon className="size-4" />
                  </Link>
                }
              >
                Find a job you like, ask your guardian, then apply. Everything you send shows up
                here so you can track it.
              </EmptyState>
            )}
        </div>
      )}
    </TeenShell>
  );
}
